// Pure functions over the stored consent decision. No Vue, no storage, no
// network: everything the banner needs to decide "is this choice still valid?"
// lives here so it can be unit-tested exhaustively.

import { CONSENT_TTL_MONTHS, COOKIE_POLICY_VERSION } from '@/content/cookies/cookieInventory'
import {
  OPTIONAL_CONSENT_CATEGORIES,
  type ConsentAction,
  type ConsentChoices,
  type StoredConsent,
} from '@/types/consent'

export const CONSENT_STORAGE_KEY = 'tpa.cookieConsent'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ACTIONS: ConsentAction[] = ['accept_all', 'reject_all', 'custom', 'withdraw']

export const NO_CONSENT: ConsentChoices = { preferences: false }
export const FULL_CONSENT: ConsentChoices = { preferences: true }

/** Random identifier for this browser (not derived from anything about the person). */
export function newSubjectId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Insecure-context fallback (crypto.randomUUID needs HTTPS or localhost).
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** Which action best describes a set of choices (used when the UI does not say). */
export function actionFor(choices: ConsentChoices): ConsentAction {
  const granted = OPTIONAL_CONSENT_CATEGORIES.filter((c) => choices[c]).length
  if (granted === OPTIONAL_CONSENT_CATEGORIES.length) return 'accept_all'
  if (granted === 0) return 'reject_all'
  return 'custom'
}

function addMonths(from: Date, months: number): Date {
  const d = new Date(from.getTime())
  d.setMonth(d.getMonth() + months)
  return d
}

export function createConsent(
  choices: ConsentChoices,
  action: ConsentAction,
  subjectId: string,
  now: Date = new Date(),
): StoredConsent {
  return {
    schema: 1,
    subjectId,
    policyVersion: COOKIE_POLICY_VERSION,
    action,
    // Copy field by field so nothing but the known booleans is ever stored.
    categories: {
      preferences: choices.preferences === true,
    },
    decidedAt: now.toISOString(),
    expiresAt: addMonths(now, CONSENT_TTL_MONTHS).toISOString(),
    synced: false,
  }
}

/**
 * Parses what localStorage returned. Strict on purpose: anything that is not
 * exactly a well-formed record (tampered, truncated, from a future schema)
 * yields null, which the UI treats as "no decision yet" and asks again. Failing
 * toward "ask" is the safe direction; failing toward "granted" would not be.
 */
export function parseStoredConsent(raw: string | null): StoredConsent | null {
  if (!raw) return null
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof data !== 'object' || data === null) return null
  const r = data as Record<string, unknown>

  if (r.schema !== 1) return null
  if (typeof r.subjectId !== 'string' || !UUID_RE.test(r.subjectId)) return null
  if (
    typeof r.policyVersion !== 'string' ||
    r.policyVersion.length === 0 ||
    r.policyVersion.length > 32
  )
    return null
  if (typeof r.action !== 'string' || !ACTIONS.includes(r.action as ConsentAction)) return null
  if (typeof r.decidedAt !== 'string' || Number.isNaN(Date.parse(r.decidedAt))) return null
  if (typeof r.expiresAt !== 'string' || Number.isNaN(Date.parse(r.expiresAt))) return null
  if (typeof r.synced !== 'boolean') return null

  const c = r.categories
  if (typeof c !== 'object' || c === null) return null
  const cats = c as Record<string, unknown>
  for (const key of OPTIONAL_CONSENT_CATEGORIES) {
    if (typeof cats[key] !== 'boolean') return null
  }

  return {
    schema: 1,
    subjectId: r.subjectId,
    policyVersion: r.policyVersion,
    action: r.action as ConsentAction,
    // Only the known categories are read back: a record written by an older
    // bundle (which also stored analytics/marketing) loses those keys here.
    categories: {
      preferences: cats.preferences as boolean,
    },
    decidedAt: r.decidedAt,
    expiresAt: r.expiresAt,
    synced: r.synced,
  }
}

/** True when the visitor decided under an older policy than the one now in force. */
export function isPolicyOutdated(record: StoredConsent | null): boolean {
  return record !== null && record.policyVersion !== COOKIE_POLICY_VERSION
}

export function isConsentExpired(record: StoredConsent, now: Date = new Date()): boolean {
  return Date.parse(record.expiresAt) <= now.getTime()
}

/** The decision is on file, current, and unexpired - the banner stays hidden. */
export function isConsentCurrent(
  record: StoredConsent | null,
  now: Date = new Date(),
): record is StoredConsent {
  return record !== null && !isPolicyOutdated(record) && !isConsentExpired(record, now)
}

/** Nothing optional is granted: the visitor said no (reject_all, withdraw, or a custom choice with everything off). */
export function isRefusal(record: StoredConsent): boolean {
  return OPTIONAL_CONSENT_CATEGORIES.every((c) => !record.categories[c])
}

/**
 * The same decision, no longer in force: the banner asks again. The record is kept rather
 * than deleted so the browser keeps its id and the next decision continues the same audit trail.
 */
export function expireConsent(record: StoredConsent, now: Date = new Date()): StoredConsent {
  return { ...record, expiresAt: now.toISOString() }
}
